import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button, FormControl, FormSelect } from 'components/common';
import { ISelectOption } from 'components/common/Select/types';
import { submissionQuestions } from './questions';
import { ISubmissionForm, ORGANISATION_IDS_BY_ENV, SubmissionFunnelTypeEnum, ViteEnv } from './types';
import { useNavigateBack } from 'hooks';
import { useMutation } from '@tanstack/react-query';
import { useApplicationInProgressById } from 'queries/applications/useApplicationInProgressById';
import { applicationFormApi } from 'api/applicationForm';
import { parseApplicationInProgress } from '../helpers';
import { useEffect, useMemo } from 'react';
import { getCurrentRoute } from 'utils/location';
import { ApplicationFormPageEnum } from '../types';
import { config } from 'utils/config';

export const Submission = () => {
  const intl = useIntl();
  const { id: applicationId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentRoute = getCurrentRoute();
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress);
  const { data: applicationInProgress } = useApplicationInProgressById(applicationId);
  const navigateBack = useNavigateBack();
  const formattedResponse =
    applicationInProgress?.[currentRoute] && parseApplicationInProgress(applicationInProgress[currentRoute]);
  const requiredFieldErrorMessage = intl.formatMessage({ id: 'common.error.requiredField' });

  const form = useForm<ISubmissionForm>({
    defaultValues: {
      submissionFunnelType: '',
      organisationName: null,
    },
  });

  useEffect(() => {
    if (formattedResponse) form.reset(formattedResponse);
  }, [applicationInProgress]);

  const formState = form.watch();

  const organisationIdsByEnv = useMemo(() => {
    return ORGANISATION_IDS_BY_ENV[config.env.VITE_ENV as ViteEnv];
  }, []);

  const organisationIdsByFunnelType = useMemo(() => {
    if (
      formState.submissionFunnelType === SubmissionFunnelTypeEnum.MORTGAGE_CLUB ||
      formState.submissionFunnelType === SubmissionFunnelTypeEnum.NETWORK
    ) {
      return organisationIdsByEnv[formState.submissionFunnelType];
    }

    return undefined;
  }, [organisationIdsByEnv, formState.submissionFunnelType]);

  const isDirectApplication =
    !formState.submissionFunnelType || formState.submissionFunnelType === SubmissionFunnelTypeEnum.DIRECT;

  const submitFormHandler = async (formData: ISubmissionForm) => {
    const filteredFormData = Object.entries(formData).filter(([key, value]) => value && key !== 'organisationId');
    const questions = filteredFormData.map(([key, answer]) => ({
      question: intl.formatMessage({ id: submissionQuestions[key].labelKey }),
      key,
      answer,
    }));

    if (formData.organisationName && organisationIdsByFunnelType) {
      const organisationId =
        organisationIdsByFunnelType[formData.organisationName as keyof typeof organisationIdsByFunnelType];

      if (organisationId) {
        questions.push({
          question: intl.formatMessage({ id: submissionQuestions.organisationId.labelKey }),
          key: 'organisationId',
          answer: organisationId,
        });
      }
    }

    await patchApplicationInProgress({ questions, currentRoute, applicationId });

    searchParams.set('section', ApplicationFormPageEnum.INTERMEDIARY_FEES);
    setSearchParams(searchParams);
  };

  const customRadioChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    form.setValue('submissionFunnelType', event.target.value as SubmissionFunnelTypeEnum);
    form.setValue('organisationName', null);
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)}>
        <div className='mt-5'>
          <FormattedMessage id={submissionQuestions.submissionFunnelType.labelKey} tagName={'h4'} />
          <FormControl
            rules={{ required: requiredFieldErrorMessage }}
            name={'submissionFunnelType'}
            radioOptions={submissionQuestions.submissionFunnelType.options}
            radioGroupProps={{ className: 'mt-2' }}
            isWide
            onChangeEffect={customRadioChangeHandler}
          />
        </div>

        {!isDirectApplication && (
          <div>
            <FormattedMessage id={submissionQuestions.organisationName.labelKey} tagName={'h4'} />
            <FormSelect
              name={'organisationName'}
              key={formState.submissionFunnelType}
              options={submissionQuestions.organisationName.options[formState.submissionFunnelType]}
              className={'w-full'}
              selectProps={{
                isOptionEqualToValue: (option: ISelectOption, selectedOptionValue: string) =>
                  option.value === selectedOptionValue,
                value: formState.organisationName,
              }}
              selectInputProps={{
                placeholder: intl.formatMessage({ id: 'placeholders.selectOrganisation' }),
              }}
              rules={{ required: requiredFieldErrorMessage }}
            />
          </div>
        )}

        <div className={'mt-8 flex justify-between'}>
          <Button variant={'outlined'} onClick={navigateBack}>
            <FormattedMessage id={'cta.back'} />
          </Button>

          <Button
            variant={'contained'}
            type={'submit'}
            disabled={!formState.submissionFunnelType || (!isDirectApplication && !formState.organisationName)}
          >
            <FormattedMessage id={'cta.continue'} />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
